import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Parallax } from "react-parallax";
import { MapPin, Clock, Shield, CalendarDays } from "lucide-react";
import { lastMatch, nextMatch, teamInfo } from "../../data/arsenalData";

const MATCH_BG =
  "https://images.pexels.com/photos/7258838/pexels-photo-7258838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const useCountdown = (targetDate) => {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = new Date(targetDate).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setTime({ d: 0, h: 0, m: 0, s: 0 });
        return;
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / 1000 / 60) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setTime({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return time;
};

export const MatchDay = () => {
  const countdown = useCountdown(nextMatch.date);

  return (
    <section id="matchday" data-testid="matchday-section" className="relative">
      <Parallax
        bgImage={MATCH_BG}
        strength={300}
        bgImageStyle={{ objectFit: "cover" }}
      >
        <div className="relative py-24 md:py-32 bg-gradient-to-br from-[#061930]/95 via-[#061930]/90 to-[#061930]/95">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(219,0,7,0.15),transparent_50%)]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="mb-14 text-center"
            >
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 bg-[#DB0007] rounded-full live-pulse" />
                <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[#9C824A]">
                  Match Day Center
                </span>
              </div>
              <h2 className="font-anton uppercase text-4xl sm:text-5xl lg:text-7xl text-white tracking-tight">
                El <span className="text-[#DB0007]">corazón</span> del juego
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-5 gap-6">
              {/* Último resultado - Main widget */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="lg:col-span-3 relative bg-[#0a2240]/90 backdrop-blur-md border border-white/10 p-6 sm:p-10"
                data-testid="last-match-widget"
              >
                <div className="absolute top-0 left-0 h-1 w-24 bg-[#DB0007]" />

                <div className="flex items-center justify-between mb-6">
                  <div className="font-outfit text-xs uppercase tracking-[0.3em] text-[#9C824A]">
                    Último Resultado
                  </div>
                  <div className="font-mono-jb text-[10px] uppercase tracking-widest text-white/50">
                    {lastMatch.competition}
                  </div>
                </div>

                <div className="grid grid-cols-5 items-center gap-4">
                  {/* Home */}
                  <div className="col-span-2 text-center">
                    <div className="w-20 h-20 sm:w-28 sm:h-28 mx-auto mb-3 flex items-center justify-center">
                      <img src={lastMatch.home.logo} alt={lastMatch.home.name} className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="font-anton uppercase text-white text-sm sm:text-base leading-tight">
                      {lastMatch.home.name}
                    </div>
                  </div>

                  {/* Score */}
                  <div className="col-span-1 text-center">
                    <div className="font-anton text-5xl sm:text-7xl text-white flex items-center justify-center gap-2 leading-none">
                      <span className={lastMatch.home.score > lastMatch.away.score ? "text-[#DB0007]" : ""}>
                        {lastMatch.home.score}
                      </span>
                      <span className="text-white/30 text-4xl">·</span>
                      <span className={lastMatch.away.score > lastMatch.home.score ? "text-[#DB0007]" : ""}>
                        {lastMatch.away.score}
                      </span>
                    </div>
                    <div className="font-mono-jb text-[10px] uppercase tracking-widest text-[#9C824A] mt-2">
                      Final
                    </div>
                  </div>

                  {/* Away */}
                  <div className="col-span-2 text-center">
                    <div className="w-20 h-20 sm:w-28 sm:h-28 mx-auto mb-3 flex items-center justify-center bg-white/5 border border-white/10">
                      <Shield size={36} className="text-[#9C824A]" />
                    </div>
                    <div className="font-anton uppercase text-white text-sm sm:text-base leading-tight">
                      {lastMatch.away.name}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between text-xs font-outfit text-white/50">
                  <div className="flex items-center gap-1">
                    <MapPin size={12} /> {lastMatch.venue}
                  </div>
                  <div className="flex items-center gap-1">
                    <CalendarDays size={12} />{" "}
                    {new Date(lastMatch.date).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                </div>
              </motion.div>

              {/* Countdown - próximo partido */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-2 relative overflow-hidden"
                data-testid="next-match-countdown"
              >
                <div className="relative h-full bg-gradient-to-br from-[#DB0007] via-[#b5000a] to-[#061930] p-6 sm:p-8 border border-[#9C824A]/30 flex flex-col">
                  <div className="absolute inset-0 grain-overlay pointer-events-none" />

                  <div className="relative">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 bg-[#9C824A] rounded-full live-pulse" />
                      <span className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-white/80">
                        Próximo Partido
                      </span>
                    </div>
                    <h3 className="font-anton uppercase text-2xl sm:text-3xl text-white leading-tight">
                      {nextMatch.home.name}
                      <span className="text-[#9C824A] mx-2">vs</span>
                      {nextMatch.away.name}
                    </h3>
                    <div className="mt-3 flex items-center gap-3 text-xs font-outfit text-white/70">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {nextMatch.venue}</span>
                    </div>
                  </div>

                  <div className="relative mt-auto pt-8">
                    <div className="grid grid-cols-4 gap-2">
                      <CountdownCell label="Días" value={countdown.d} />
                      <CountdownCell label="Hrs" value={countdown.h} />
                      <CountdownCell label="Min" value={countdown.m} />
                      <CountdownCell label="Seg" value={countdown.s} />
                    </div>
                    <div className="font-mono-jb text-[10px] uppercase tracking-widest text-white/60 mt-4 flex items-center gap-2">
                      <Clock size={12} />
                      {new Date(nextMatch.date).toLocaleString("es-MX", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        hour: "numeric",
                        minute: "numeric",
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </Parallax>
    </section>
  );
};

const CountdownCell = ({ label, value }) => (
  <div className="bg-black/30 border border-white/10 text-center p-3 backdrop-blur-sm">
    <div className="font-anton text-3xl sm:text-4xl text-white tabular-nums leading-none">
      {String(value).padStart(2, "0")}
    </div>
    <div className="font-mono-jb text-[9px] uppercase tracking-widest text-white/60 mt-1">
      {label}
    </div>
  </div>
);
